// Considered: FilterChipsDisplayMolecule — rejected because it renders one
// flat, uniformly-editable chip list with no heading or permission-gated
// action slot. A provenance group's content is that heading + action link
// wrapped around a chip list whose editability varies by origin, so this
// molecule COMPOSES FilterChipsDisplayMolecule (`showHeader={false}`) rather
// than extending it with concerns FilterChipsDisplayMolecule's other callers
// don't need. See spec Reuse Audit (specs/per-level-context-filters.md).
import React from 'react';
import { useTranslation } from 'localization';
import type { FilterExpression, UnifiedFilters } from 'types/filters';
import type { FilterLayer } from 'types/contextView';
import type { OrgAvailableTypeSchema } from 'types/schema';
import FilterChipsDisplayMolecule from '../FilterChipsDisplay/FilterChipsDisplayMolecule';
import EditableFilterRowMolecule from '../EditableFilterRow/EditableFilterRowMolecule';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import { perspectiveUnifiedToState } from '../../../utils/contextFlowSerialization';
import './FilterProvenanceGroupMolecule.scss';

export interface FilterProvenanceGroupMoleculeProps {
  /** Which layer this group renders — drives the heading copy and lock state. */
  layer: FilterLayer;
  /** This layer's own clauses (already scoped to the popover's property, if focused). */
  filters: UnifiedFilters;
  /**
   * Whether this group's chips render locked. Always `true` for `layer === 'context'`
   * (read-only by construction); `true` for `view` when the caller lacks
   * `context_view.edit`, matching "a constraint they cannot change is still a
   * constraint they must be able to see" (spec Edge Cases).
   */
  locked: boolean;
  /** Scope qualifier shown beside the heading — e.g. "applies to every repeat" for `repeat > 1` levels. */
  scopeNote?: string;
  /**
   * Permission-gated action link — e.g. "Edit in context". Omitted entirely
   * (never disabled) when the caller lacks the permission it requires: an
   * action the user can never take is noise, not a call to action.
   */
  action?: { label: string; onClick: () => void };
  getPropertyLabel?: (propertyKey: string) => string;
  getPerspectiveLabel?: (perspectiveSystemName: string) => string;
  getRelationshipLabel?: (relationshipSystemName: string) => string;
  getPropertyValueLabel?: (propertyKey: string, value: string) => string;
  /** When false, suppresses the built-in heading row (use when the caller renders its own tier header). Default true. */
  showHeading?: boolean;
  /**
   * `'chips'` (default) renders the read-only chip list via `FilterChipsDisplayMolecule`
   * (every existing caller). `'rows'` renders each property filter as a full
   * `EditableFilterRowMolecule` instead — the shape the column adapter's view tier
   * needs once `canEditSharedView` makes the row genuinely editable in place
   * (uf-column's `<EditableFilterRow>` leaf, `filter-row.jsx:622-737`). Ignored
   * (falls back to chips) unless `rowsProps` is also supplied.
   */
  variant?: 'chips' | 'rows';
  /**
   * Required when `variant === 'rows'` — the editable-row wiring `FilterChipsDisplayMolecule` never needed.
   *
   * Rows are identified by their filter's `id`, never by position: `filters`
   * is often a narrowed slice of the layer the caller writes back to (a
   * column popover passes only its own property's clauses), so a position in
   * this list says nothing about the clause's position in the caller's list.
   * Every property filter passed in rows mode must therefore carry an `id`
   * (normalise with `withStableIds` on the caller's FULL list, then narrow);
   * a filter without one renders as a read-only chip instead of a row, since
   * no edit to it could be routed back.
   */
  rowsProps?: {
    availableProperties: PropertyOption[];
    onUpdate: (filterId: string, updated: FilterExpression) => void;
    onRemove: (filterId: string) => void;
    arcTemplates?: ArcTemplateOption[];
    availableRelationships?: RelationshipOption[];
    familyTypeSchemas?: OrgAvailableTypeSchema[];
    availablePerspectives?: PerspectiveOption[];
    perspectiveSearchProps?: {
      schemaSystemName: string;
      sphereId: string;
      direction?: 'outwards' | 'inwards' | 'both';
      hops?: number;
    };
  };
  className?: string;
  /** Parity instrumentation only — the chip list's provenance badge (`uf-column.pill#2`/`.pill#4`). */
  pillDatumId?: string;
}

const PROVENANCE_BADGE: Partial<Record<FilterLayer, string>> = {
  context: 'CONTEXT',
  view: 'VIEW',
  user: 'USER',
};

const HEADING_KEY: Record<FilterLayer, string> = {
  context: 'columnFilters.provenance.context.heading',
  view: 'columnFilters.provenance.view.heading',
  user: 'columnFilters.provenance.user.heading',
  session: 'columnFilters.provenance.session.heading',
  link: 'columnFilters.provenance.link.heading',
};

const REASON_KEY: Record<FilterLayer, string> = {
  context: 'columnFilters.provenance.context.lockedReason',
  view: 'columnFilters.provenance.view.lockedReason',
  user: 'columnFilters.provenance.user.lockedReason',
  session: 'columnFilters.provenance.session.lockedReason',
  link: 'columnFilters.provenance.link.lockedReason',
};

const FilterProvenanceGroupMolecule: React.FC<FilterProvenanceGroupMoleculeProps> = ({
  layer,
  filters,
  locked,
  scopeNote,
  action,
  getPropertyLabel,
  getPerspectiveLabel,
  getRelationshipLabel,
  getPropertyValueLabel,
  showHeading = true,
  variant = 'chips',
  rowsProps,
  className = '',
  pillDatumId,
}) => {
  const { t } = useTranslation();

  const heading = showHeading && (
    <div className="filter-provenance-group__heading">
      <span className="filter-provenance-group__heading-text">{t(HEADING_KEY[layer])}</span>
      {scopeNote && <span className="filter-provenance-group__scope-note">{scopeNote}</span>}
      {action && (
        <button
          type="button"
          className="filter-provenance-group__action"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  );

  if (variant === 'rows' && rowsProps) {
    // `rowsProps` edits identified `propertyFilters` only, so the tier's other
    // lanes (and any property filter with no id to route an edit by) render
    // as chips beneath the rows: shown, with no dismiss affordance (no
    // `onRemove*`), since this group has no write path for them.
    const propertyFilters = filters.propertyFilters ?? [];
    const identifiedRows = propertyFilters.filter(
      (filter): filter is FilterExpression & { id: string } => Boolean(filter.id)
    );
    const unidentifiedRows = propertyFilters.filter((filter) => !filter.id);
    const hasOtherLanes =
      unidentifiedRows.length > 0 ||
      (filters.perspectiveFilters?.length ?? 0) > 0 ||
      (filters.relationshipFilters?.length ?? 0) > 0 ||
      (filters.arcFilters?.length ?? 0) > 0;
    return (
      <div
        className={`filter-provenance-group filter-provenance-group--${layer} filter-provenance-group--rows ${className}`.trim()}
        // One row per filter of this tier — the prototype's `each(viewFilters)` /
        // `each(contextFilters)` collection, which PAR-5 requires the render to declare.
        data-container={`each(${layer}Filters)`}
      >
        {heading}
        {identifiedRows.map((filter) => (
          <EditableFilterRowMolecule
            key={filter.id}
            filter={filter}
            availableProperties={rowsProps.availableProperties}
            onChange={(updated) => rowsProps.onUpdate(filter.id, updated)}
            onRemove={() => rowsProps.onRemove(filter.id)}
            arcTemplates={rowsProps.arcTemplates}
            availableRelationships={rowsProps.availableRelationships}
            familyTypeSchemas={rowsProps.familyTypeSchemas}
            availablePerspectives={rowsProps.availablePerspectives}
            perspectiveSearchProps={rowsProps.perspectiveSearchProps}
            parityBlock="uf-column"
            toggleDatumId="uf-column.button"
            removeDatumId="uf-column.button#2"
            propertyDatumId="uf-column.property"
            conditionDatumId="uf-column.condition"
            opDatumId="uf-column.op"
            valueDatumId="uf-column.value#3"
            applyDatumId="uf-column.apply"
            summaryValueDatumId="uf-column.value#2"
          />
        ))}
        {hasOtherLanes && (
          <FilterChipsDisplayMolecule
            propertyFilters={unidentifiedRows}
            perspectiveFilters={(filters.perspectiveFilters ?? []).map(perspectiveUnifiedToState)}
            relationshipFilters={filters.relationshipFilters}
            arcFilters={filters.arcFilters}
            getPropertyLabel={getPropertyLabel}
            getPropertyValueLabel={getPropertyValueLabel}
            getPerspectiveLabel={getPerspectiveLabel}
            getRelationshipLabel={getRelationshipLabel}
            showHeader={false}
            showClearAll={false}
            provenanceBadge={PROVENANCE_BADGE[layer]}
            provenanceBadgeVariant={layer as 'context' | 'view' | 'user' | 'session'}
            provenanceBadgeDatumId={pillDatumId}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`filter-provenance-group filter-provenance-group--${layer} ${className}`.trim()}
      data-container={`each(${layer}Filters)`}
    >
      {heading}
      <FilterChipsDisplayMolecule
        propertyFilters={filters.propertyFilters}
        perspectiveFilters={(filters.perspectiveFilters ?? []).map(perspectiveUnifiedToState)}
        relationshipFilters={filters.relationshipFilters}
        arcFilters={filters.arcFilters}
        filterGroups={filters.filterGroups}
        getPropertyLabel={getPropertyLabel}
        getPerspectiveLabel={getPerspectiveLabel}
        getRelationshipLabel={getRelationshipLabel}
        getPropertyValueLabel={getPropertyValueLabel}
        showHeader={false}
        showClearAll={false}
        locked={locked}
        lockedReason={t(REASON_KEY[layer])}
        provenanceBadge={PROVENANCE_BADGE[layer]}
        provenanceBadgeVariant={layer as 'context' | 'view' | 'user' | 'session'}
        provenanceBadgeDatumId={pillDatumId}
      />
    </div>
  );
};

export default FilterProvenanceGroupMolecule;
